// Activo Fijo → Depreciación → Períodos (cierre/reapertura y contabilización).
//
// El cierre SELLA la corrida (script 788): guarda una huella del contenido, y
// quién cerró y cuándo. Reabrir exige un motivo y queda firmado. El acta
// recalcula la huella, así que delata si alguien tocó las cifras después del
// cierre — que es justo lo que un auditor viene a preguntar.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Loader2, Lock, ScrollText, ShieldAlert, ShieldCheck, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { CorridaActa, CorridaRow, ESTADO_CORRIDA_CLS, MESES, afRpc, money } from './shared';

const soloFecha = (v?: string | null) => (v ? String(v).slice(0, 10) : '');

export function PeriodosAF() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [acta, setActa] = useState<string | null>(null);   // id de la corrida cuya acta se mira

  const { data: rows = [], isFetching } = useQuery<CorridaRow[]>({
    queryKey: ['af-corridas'],
    queryFn: () => afRpc<CorridaRow[]>('af_corridas', { _anio: null }),
  });
  const inval = () => { void qc.invalidateQueries({ queryKey: ['af-corridas'] }); void qc.invalidateQueries({ queryKey: ['af-activos'] }); };

  const contabilizar = useMutation({
    mutationFn: (id: string) => afRpc('af_corrida_contabilizar', { _corrida_id: id }),
    onSuccess: () => { toast.success('Corrida contabilizada'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: (id: string) => afRpc('af_corrida_cerrar', { _corrida_id: id }),
    onSuccess: () => { toast.success('Período cerrado y sellado'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reabrir = useMutation({
    mutationFn: (v: { id: string; motivo: string }) => afRpc('af_corrida_reabrir', { _corrida_id: v.id, _motivo: v.motivo }),
    onSuccess: () => { toast.success('Período reabierto'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pedirMotivo = async (c: CorridaRow) => {
    const v = await dlg.pedir(`Reabrir ${MESES[c.mes - 1]} ${c.anio}`,
      [{ key: 'motivo', label: 'Motivo de la reapertura', multilinea: true, minimo: 10 }],
      { detalle: 'El cierre anterior NO se borra: queda quién lo cerró y cuándo. Queda registrado con tu usuario (mínimo 10 caracteres).' });
    if (!v) return;
    reabrir.mutate({ id: c.id, motivo: v.motivo.trim() });
  };

  const selloTitulo = (c: CorridaRow) => {
    const p: string[] = [];
    if (c.cerrada_at) p.push(`Cerrado ${soloFecha(c.cerrada_at)}${c.cerrada_por ? ` por ${c.cerrada_por}` : ''}`);
    if (c.reabierta_at) p.push(`Reabierto ${soloFecha(c.reabierta_at)}${c.reabierta_por ? ` por ${c.reabierta_por}` : ''}${c.motivo_reapertura ? `: ${c.motivo_reapertura}` : ''}`);
    return p.length ? p.join('\n') : 'Sin cierre registrado';
  };

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><Lock className="w-5 h-5" /> Períodos de depreciación</h1>
      <p className="text-sm text-muted-foreground">Corridas por período. Contabiliza las que quedaron en borrador (tras mapear cuentas), cierra las contabilizadas para bloquear reprocesos, o reabre para corregir. Cerrar y reabrir exigen <b>nivel 5</b>; la reapertura pide motivo y queda firmada.</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Corridas ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Período</th><th className="py-2 pr-3 text-right">Unidades</th><th className="py-2 pr-3 text-right">Total depreciación</th><th className="py-2 pr-3">Asiento</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Cierre</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin corridas registradas</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{MESES[c.mes - 1]} {c.anio}</td>
                  <td className="py-2 pr-3 text-right">{c.n_activos}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.total_depreciacion)}</td>
                  <td className="py-2 pr-3">{c.asiento_numero ? <span className="inline-flex items-center gap-1 text-primary"><FileText className="w-3.5 h-3.5" /> #{c.asiento_numero}</span> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CORRIDA_CLS[c.estado] ?? 'bg-muted'}`}>{c.estado}</span></td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap" title={selloTitulo(c)}>
                    {c.cerrada_at ? (
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                        {soloFecha(c.cerrada_at)}{c.cerrada_por ? ` · ${c.cerrada_por}` : ''}
                      </span>
                    ) : '—'}
                    {c.reabierta_at && (
                      <span className="ml-2 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400" title={c.motivo_reapertura ?? ''}>
                        <Unlock className="h-3.5 w-3.5" /> reabierto
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <div className="inline-flex gap-2">
                      {c.cerrada_at && (
                        <Button size="sm" variant="ghost" onClick={() => setActa(c.id)} title="Acta del período (para el papel de trabajo)">
                          <ScrollText className="w-4 h-4" />
                        </Button>
                      )}
                      {c.estado === 'borrador' && <Button size="sm" variant="outline" isLoading={contabilizar.isPending} onClick={() => contabilizar.mutate(c.id)}><CheckCircle2 className="w-4 h-4 mr-1" /> Contabilizar</Button>}
                      {c.estado === 'contabilizada' && <Button size="sm" variant="outline" onClick={async () => { if (await dlg.confirmar('¿Cerrar el período?', { detalle: 'Bloquea reprocesos y guarda una huella del contenido.' })) cerrar.mutate(c.id); }}><Lock className="w-4 h-4 mr-1" /> Cerrar</Button>}
                      {c.estado === 'cerrada' && <Button size="sm" variant="outline" onClick={() => void pedirMotivo(c)}><Unlock className="w-4 h-4 mr-1" /> Reabrir</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {acta && <ActaPeriodo corridaId={acta} onClose={() => setActa(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------- el acta ----
function ActaPeriodo({ corridaId, onClose }: { corridaId: string; onClose: () => void }) {
  const { data, isFetching } = useQuery<CorridaActa>({
    queryKey: ['af-corrida-acta', corridaId],
    queryFn: () => afRpc<CorridaActa>('af_corrida_acta', { _corrida_id: corridaId }),
  });

  return (
    <PanelIntegrado title="Acta del período" onClose={onClose}>
      <div className="space-y-4">
        {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          {data && (
            <>
              {/* Lo único que de verdad importa del acta. */}
              <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${data.intacta
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : 'border-red-500/50 bg-red-500/5'}`}>
                {data.intacta
                  ? <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  : <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />}
                <div>
                  <p className="font-medium">{data.intacta
                    ? 'Las cifras son las mismas que se cerraron.'
                    : 'Las cifras CAMBIARON después del cierre.'}</p>
                  <p className="text-xs text-muted-foreground">
                    {data.intacta
                      ? 'La huella guardada al cerrar coincide con la que se recalcula ahora.'
                      : 'La huella guardada al cerrar no coincide con la actual. Revisa la pista de auditoría de la corrida.'}
                  </p>
                </div>
              </div>

              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><dt className="text-xs uppercase text-muted-foreground">Período</dt><dd>{MESES[data.mes - 1]} {data.anio}</dd></div>
                <div><dt className="text-xs uppercase text-muted-foreground">Estado</dt><dd>{data.estado}</dd></div>
                <div><dt className="text-xs uppercase text-muted-foreground">Unidades depreciadas</dt><dd className="tabular-nums">{data.n_activos}</dd></div>
                <div><dt className="text-xs uppercase text-muted-foreground">Total</dt><dd className="font-mono tabular-nums">{money(data.total_depreciacion)}</dd></div>
                <div><dt className="text-xs uppercase text-muted-foreground">Cerrado por</dt><dd>{data.cerrada_por ?? '—'}{data.cerrada_at ? ` · ${soloFecha(data.cerrada_at)}` : ''}</dd></div>
                <div><dt className="text-xs uppercase text-muted-foreground">Reabierto por</dt><dd>{data.reabierta_por ?? '—'}{data.reabierta_at ? ` · ${soloFecha(data.reabierta_at)}` : ''}</dd></div>
              </dl>

              {data.motivo_reapertura && (
                <div className="rounded-md border bg-muted/40 p-3 text-sm">
                  <div className="text-xs uppercase text-muted-foreground">Motivo de la reapertura</div>
                  <p className="mt-0.5">{data.motivo_reapertura}</p>
                </div>
              )}

              <div className="text-xs text-muted-foreground">
                <div>Huella al cerrar: <span className="font-mono break-all">{data.hash_cierre ?? '—'}</span></div>
                <div>Huella actual: <span className="font-mono break-all">{data.hash_actual}</span></div>
              </div>

              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Placa</th><th className="px-3 py-2">Activo / componente</th>
                    <th className="px-3 py-2 text-right">Cuota</th><th className="px-3 py-2 text-right">Acumulada</th>
                  </tr></thead>
                  <tbody>
                    {data.unidades.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Sin unidades en la corrida</td></tr>}
                    {data.unidades.map((u, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-3 py-1.5 font-mono">{u.placa}</td>
                        <td className="px-3 py-1.5">{u.unidad}</td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">{money(u.importe)}</td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">{money(u.acumulada)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
      </div>
    </PanelIntegrado>
  );
}
